/**
 * TanStack Query setup + THE single global 401 handler.
 *
 * "Три копии обработчика 401 — это три разных бага" (plans §7.5). There is exactly one here, and
 * it lives at the seam (`http.ts` calls back into it), so every fetch — queries, mutations, and
 * the XHR uploader alike — funnels through the same redirect.
 */
import { QueryClient } from '@tanstack/react-query';
import { LOGIN_PATH } from '@most/auth';
import { ApiError, NetworkError, setUnauthorizedHandler } from './http';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        // A 4xx is a decision, not a hiccup — retrying a 422 just spams the server with the same
        // invalid body. Only transient failures are worth another go.
        if (error instanceof ApiError) {
          return error.status >= 500 && failureCount < 2;
        }
        if (error instanceof NetworkError) return failureCount < 2;
        return false;
      },
    },
    mutations: {
      // Never auto-retry a mutation: at best it is wasted, at worst it double-submits.
      retry: false,
    },
  },
});

/**
 * Session expiry. The cookie is HttpOnly, so the SPA cannot inspect it — a 401 from any call is
 * the only signal we get. Clear the cache (it may hold another user's data if the session was
 * swapped) and bounce to the login page on the LANDING app, preserving `next` so the producer
 * lands back where they were.
 *
 * `/login` lives on the landing app (`apps/landing`), not here, so this is a hard navigation
 * out of the SPA and not a React Router push.
 */
export function installUnauthorizedHandler(): void {
  let redirecting = false;

  setUnauthorizedHandler(() => {
    // A dashboard fires several parallel queries; a dead session 401s all of them. Redirect once.
    if (redirecting) return;
    redirecting = true;

    queryClient.clear();

    // No `?next=`: the login page routes by role and never read it, and since D16 it lives on
    // another origin, where echoing a query-string redirect target back would be an open
    // redirect.
    window.location.assign(LOGIN_PATH);
  });
}

/** Query keys, centralized so invalidations cannot typo their way into a stale screen. */
export const qk = {
  me: ['me'] as const,
  config: ['config'] as const,
  categories: ['categories'] as const,
  profile: ['producer', 'profile'] as const,
  products: (params: Record<string, unknown>) => ['producer', 'products', params] as const,
  product: (id: string) => ['producer', 'product', id] as const,
  orders: (params: Record<string, unknown>) => ['producer', 'orders', params] as const,
  order: (number: string) => ['producer', 'order', number] as const,
  notifications: ['notifications'] as const,
  telegramStatus: ['telegram', 'status'] as const,
  chat: ['chat'] as const,
  chatMessages: ['chat', 'messages'] as const,
};
